import { Box, Link, Typography, useMediaQuery } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { parseTipiAttachmentName } from '../../../../../resources/discussion/operations';
import { MessageAttachment } from '../../../../../resources/discussion/types';

type TipiCompactRowProps = {
    attachment: MessageAttachment;
    onTipiLinkClick: () => void;
};

export const TipiCompactRow = ({ attachment, onTipiLinkClick }: TipiCompactRowProps) => {
    const supportsHover = useMediaQuery('(hover: hover)');
    const { formatMessage, formatDate } = useIntl();
    const { date, label } = parseTipiAttachmentName(attachment.name);

    return (
        <Box
            component="li"
            data-testid="attachment-tipi-link-compact"
            data-spec-id="attachment-tipi-link-compact"
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 0.5,
                columnGap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: 'background.variant',
                ...(supportsHover && {
                    '&:hover [data-tipi-hover-link], &:focus-within [data-tipi-hover-link]': {
                        opacity: 1,
                    },
                }),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    columnGap: 1,
                    flex: '1 1 auto',
                    minWidth: 0,
                }}
            >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {label}
                </Typography>
                {date && (
                    <Typography variant="body2" color="text.secondary">
                        {formatMessage(
                            { id: 'discussion.attachments_list.created_on_date' },
                            {
                                date: formatDate(date, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                }),
                            },
                        )}
                    </Typography>
                )}
            </Box>
            <Typography
                data-tipi-hover-link
                variant="body2"
                sx={{
                    flexShrink: 0,
                    opacity: supportsHover ? 0 : 1,
                    transition: 'opacity 150ms',
                }}
            >
                <Link
                    href={attachment.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onTipiLinkClick}
                    sx={{ fontWeight: 700 }}
                >
                    {formatMessage({ id: 'discussion.attachments_list.see_in_tipi_link' })}
                </Link>
            </Typography>
        </Box>
    );
};
